import { ApolloError, useMutation, useQuery, useSubscription } from '@apollo/client';
import { cancel, notify } from '../../helpers/toastify';
import useCustomLocalStorage from '../../customHooks/useCustomLocalStorage';

import BillingDashboard from '../organisms/BillingDashboard';
import { TitlePart } from '@bloomays-lib/ui.shared';
import { SingleLineText } from '@bloomays-lib/ui.shared';
import {
  CREATE_BILLING_CUSTOMER_INVOICE,
  JOBS_COMPLETED_SUBSCRIPTION,
  JOBS_ERRORED_SUBSCRIPTION,
  RECRUITEMENTS,
  UPSERT_BILLING,
} from '@bloomays-lib/adapter.api-bloomer';
import { Billing, InvoiceStatus, Job } from '@bloomays-lib/types.shared';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Logger } from '../../services/serviceLogger';
const logger = Logger('BillingActivity');

interface BillingActivityVars {
  isTrialPeriod: boolean;
}
interface BillingActivityData {
  getRecruitements: Billing[];
}

interface UpsertBillingVars {
  input: Partial<Billing>;
}
interface BillingActivityData {
  upsertBilling: Billing;
}

const BillingActivity = () => {
  const { t } = useTranslation(['notify']);

  const [refresher, setRefresher] = useState<number>(0);
  const [isTrialPeriod, setIsTrialPeriod] = useCustomLocalStorage('isTrialPeriod', false);

  const { data, loading, error, refetch } = useQuery<BillingActivityData, BillingActivityVars>(RECRUITEMENTS, {
    variables: {
      isTrialPeriod,
    },
    errorPolicy: 'all',
  });

  const [rowsBilling, setRowsBilling] = useState<Billing[]>(data?.getRecruitements || []);

  const onDataSubscription = (newStatus: InvoiceStatus, jobId?: string, jobQueueName?: string) => {
    if (jobQueueName === 'createBillingCustomerInvoice' && jobId) {
      const recordId = jobId.split('-')[1];
      void refetch().then((refreshdata) => {
        logger.debug('refetch createBillingCustomerInvoice', refreshdata);
        setRefresher((prev) => prev + 1);
        const newRows = refreshdata.data.getRecruitements || [];
        const { item: newRow } = findBilling(recordId, newRows);
        if (newRow?.invoiceStatus) {
          setRowsBilling((prev) => {
            return updateRowsStateStatus(prev, recordId, newRow.invoiceStatus);
          });
        }
      });
    }
  };

  const { data: dataSubscriptionError } = useSubscription<{
    jobErrored: { jobId: string; jobQueueName: string; jobError: { message: string } };
  }>(JOBS_ERRORED_SUBSCRIPTION, {
    onData: (v) => onDataSubscription('En erreur', v.data.data?.jobErrored.jobId, v.data.data?.jobErrored.jobQueueName),
  });

  const { data: dataSubscriptionComplete } = useSubscription<{ jobCompleted: { jobId: string; jobQueueName: string } }>(
    JOBS_COMPLETED_SUBSCRIPTION,
    {
      onData: (v) =>
        onDataSubscription('En erreur', v.data.data?.jobCompleted.jobId, v.data.data?.jobCompleted.jobQueueName),
    },
  );

  useEffect(() => {
    if (dataSubscriptionError?.jobErrored) {
      notify('error', 'Traitement executé mais avec des erreurs', dataSubscriptionError.jobErrored.jobError.message, {
        hideProgressBar: true,
        autoClose: false,
        toastId: `dataSubscriptionComplete-${dataSubscriptionError.jobErrored.jobId}`,
      });
    }
  }, [dataSubscriptionError]);

  useEffect(() => {
    if (dataSubscriptionComplete?.jobCompleted) {
      notify('success', 'Traitement executé avec succès', dataSubscriptionComplete.jobCompleted.jobId, {
        hideProgressBar: true,
        autoClose: false,
        toastId: `dataSubscriptionComplete-${dataSubscriptionComplete.jobCompleted.jobId}`,
      });
    }
  }, [dataSubscriptionComplete]);

  const [
    createBillingCustomerInvoice,
    { loading: loadingCreateBillingCustomerInvoice, error: errorCreateBillingCustomerInvoice },
  ] = useMutation<
    {
      createBillingCustomerInvoice: Job;
    },
    {
      billingRecordID: string;
      force: boolean;
    }
  >(CREATE_BILLING_CUSTOMER_INVOICE);

  const sendNotification = (type: 'info' | 'success', message: string) => {
    notify(type, message, null, {
      hideProgressBar: true,
      autoClose: 1000,
    });
  };

  const sendNotificationError = (error: ApolloError, message: string) => {
    errorLogger(error, {
      extraInfos: message,
    });
    notify('error', 'Erreur lors de la regénération de la facture client', error);
  };

  useEffect(() => {
    loadingCreateBillingCustomerInvoice && sendNotification('info', 'Regénération de la facture client en cours ! :)');
  }, [loadingCreateBillingCustomerInvoice]);

  useEffect(() => {
    if (errorCreateBillingCustomerInvoice) {
      sendNotificationError(errorCreateBillingCustomerInvoice, 'Erreur lors de la regénération de la facture client');
    }
  }, [errorCreateBillingCustomerInvoice]);

  useEffect(() => {
    const dataLoaded = data?.getRecruitements;
    if (dataLoaded) {
      setRowsBilling(dataLoaded);
      logger.debug('useEffect rows', dataLoaded);
    }
  }, [data?.getRecruitements]);

  const [upsertBilling] = useMutation<BillingActivityData, UpsertBillingVars>(UPSERT_BILLING);

  useEffect(() => {
    if (error) {
      errorLogger(error, {
        extraInfos: 'Internal server error, impossible to get recruitements !',
        state: {},
      });
      cancel();
      notify('error', 'Certaines données sont fausses! 😓', error);
    }
  }, [error]);

  const updateBilling = async (billing: Partial<Billing>): Promise<Partial<Billing>> => {
    notify('info', `${t('updateInProgress')}`);
    try {
      const dataBilling = await upsertBilling({
        variables: { input: billing },
      });
      if (dataBilling?.data?.upsertBilling) {
        cancel();
        notify('success', `${t('updateSuccess')}`);
        return dataBilling.data.upsertBilling;
      }
      throw new Error('No data billing');
    } catch (e: any) {
      cancel();
      notify('error', `${t('randomUpdateError')}`, e);
      throw e;
    }
  };

  const findBilling = (billingRecordId: string, prev: Billing[]) => {
    const index = prev.findIndex((item) => item.recordId === billingRecordId);
    if (index === -1) {
      return { item: null, index: -1 };
    }
    return { item: prev[index], index };
  };

  const updateRowsStateStatus = (prev: Billing[], billingRecordId: string, newStatus: InvoiceStatus) => {
    const { index } = findBilling(billingRecordId, prev);
    if (index !== -1) {
      return prev.map((item, i) => {
        if (i === index) {
          return { ...item, invoiceStatus: newStatus };
        }
        return item;
      });
    }
    return prev;
  };

  return (
    <div>
      <TitlePart id="CDIActivityTitle" span="Dashboard de l'activité en CDI" />
      <SingleLineText
        id="descriptionCDIDasboard"
        text="Ici tu retrouveras toutes les missions de nos clients et bloomers en CDI !"
      />
      <ContainerDiv>
        <BillingDashboard
          loading={loading}
          billings={rowsBilling}
          isTrialPeriod={isTrialPeriod}
          key={refresher}
          onChange={() => setIsTrialPeriod(!isTrialPeriod)}
          updateBilling={updateBilling}
          createBillingCustomerInvoice={async (v: { variables: { billingRecordId: string; force: boolean } }) => {
            let prevStatus: InvoiceStatus;
            setRowsBilling((prev) => {
              const { item } = findBilling(v.variables.billingRecordId, prev);
              prevStatus = item?.invoiceStatus || 'Non créée';
              return updateRowsStateStatus(prev, v.variables.billingRecordId, 'En cours');
            });
            await createBillingCustomerInvoice({
              variables: {
                billingRecordID: v.variables.billingRecordId,
                force: v.variables.force,
              },
            }).catch((error) => {
              setRowsBilling((prev) => {
                return updateRowsStateStatus(prev, v.variables.billingRecordId, prevStatus);
              });
            });
          }}
        />
      </ContainerDiv>
    </div>
  ) as React.ReactNode;
};

const ContainerDiv = styled('div')(({ theme }) => ({
  margin: 'auto',
  width: '90%',
}));

export default BillingActivity;
